<script setup lang="ts">
import { computed } from 'vue'

import { Flex, Text, type FlexProps } from '@/components'

import * as styles from './Button.css'

export interface ButtonProps extends FlexProps {
  /* Whether the button is disabled */
  disabled?: boolean

  /** Treatment of the button fill-style. */

  treatment?: 'fill' | 'outline'

  /** Variant of the button. */

  variant?: 'accent' | 'primary' | 'secondary' | 'negative'
}

const { disabled, treatment = 'fill', variant = 'primary', ...rest } = defineProps<ButtonProps>()

const backgroundColor = computed(() => {
  if (disabled) {
    return 'tertiary100'
  }

  switch (variant) {
    case 'accent':
      // TODO: needs a proper hover state, but we will have to add a new color
      // secondary300
      return { default: 'secondary200', hover: 'secondary200' } as const
    case 'primary':
      if (treatment === 'fill') {
        return { default: 'primary200', hover: 'primary300' } as const
      } else if (treatment === 'outline') {
        return { default: 'tertiary50', hover: 'primary100' } as const
      }
      break
    case 'secondary':
      if (treatment === 'fill') {
        return { default: 'tertiary500', hover: 'tertiary600' } as const
      } else if (treatment === 'outline') {
        return { default: 'tertiary50', hover: 'tertiary75' } as const
      }
      break
    case 'negative':
      if (treatment === 'fill') {
        // TODO: needs a proper hover state, but we will have to add a new color
        // Solution could be to rename the existing error color to error200
        return { default: 'error', hover: 'error' } as const
      } else if (treatment === 'outline') {
        return { default: 'tertiary50', hover: 'error' } as const
      }
      break
  }

  return 'tertiary50'
})

const borderColor = computed(() => {
  if (disabled) {
    return 'tertiary100'
  }

  if (variant === 'primary') {
    if (treatment === 'fill') {
      return { default: 'primary200', hover: 'primary300' } as const
    } else if (treatment === 'outline') {
      return { default: 'primary200', hover: 'primary200' } as const
    }
  }

  if (variant === 'secondary') {
    if (treatment === 'fill') {
      return { default: 'tertiary500', hover: 'tertiary600' } as const
    } else if (treatment === 'outline') {
      return 'tertiary600'
    }

    return 'tertiary600'
  }

  if (variant === 'negative' && treatment === 'outline') {
    return 'error'
  }

  return 'tertiary50'
})

const color = computed(() => {
  if (disabled) {
    return 'tertiary400'
  }

  if (treatment === 'outline') {
    if (variant === 'primary') {
      return 'primary200'
    }

    if (variant === 'secondary') {
      return 'tertiary600'
    }

    if (variant === 'negative' && treatment === 'outline') {
      return { default: 'error', hover: 'tertiary50' } as const
    }
  }

  return 'tertiary50'
})
</script>

<template>
  <Flex
    v-bind="rest"
    align-items="center"
    :as-child="!as"
    :background-color="backgroundColor"
    :border-radius="30"
    :border-color="borderColor"
    border-style="solid"
    :border-width="2"
    :class="styles.button"
    :color="color"
    :cursor="disabled ? 'not-allowed' : 'pointer'"
    :font-size="30"
    :gap="20"
    justify-content="center"
    :padding-block="20"
    :padding-inline="70"
    position="relative"
    outline="none"
  >
    <Text v-if="!as" as-child weight="medium" white-space="nowrap">
      <button :disabled="disabled">
        <slot />
      </button>
    </Text>
    <slot v-else />
  </Flex>
</template>
